import {
  FluentProvider,
  webLightTheme,
  webDarkTheme,
  Theme,
  Text,
  makeStyles,
  tokens,
  Switch,
} from '@fluentui/react-components';
import OfficeMonolineIcon from './assets/shared/Images/OfficeMonoline.svg';
import { useState, useEffect } from 'react';
import {
  Code20Regular,
  ChevronRight20Regular,
  TextBulletListTreeRegular,
  History20Regular,
  CompassNorthwestFilled,
  PersonFeedbackFilled,
  Chat20Regular,
} from '@fluentui/react-icons';
import { APP_VERSION } from './version';
import './index.css';

// Import profile photos
import kdisneyPhoto from './assets/shared/Images/kdisney.png';
import kandersonPhoto from './assets/shared/Images/kanderson.png';

// Office Logo Component (simplified monoline style)
const OfficeLogo = ({ isDark }: { isDark: boolean }) => {
  return (
    <img 
      src={OfficeMonolineIcon} 
      alt="Office Logo" 
      width={28} 
      height={28}
      style={{ 
        width: '28px', 
        height: '28px',
        filter: isDark ? 'brightness(0) invert(1)' : 'none'
      }}
    />
  );
};

import wordIcon from './assets/shared/Images/Word.svg';
import formsIcon from './components/apps/forms/Size=32, Theme=Color.svg';

// Office App Icon Components using local Microsoft 365 product icons
const WordIcon = () => {
  return (
    <img
      src={wordIcon}
      width="32"
      height="32"
      alt="Word product icon"
      style={{ display: 'block' }}
    />
  );
};



const FormsIcon = () => {
  return (
    <img
      src={formsIcon}
      width="32"
      height="32"
      alt="Forms product icon"
      style={{ display: 'block' }}
    />
  );
};

const useStyles = makeStyles({
  container: {
    backgroundColor: tokens.colorNeutralBackground1, // Changed to white background
    minHeight: '100vh',
    fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
    paddingBottom: '20px', // Added bottom padding to ensure there's always space at the bottom
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '27px 88px 17px',
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    '@media (max-width: 768px)': {
      padding: '20px 20px 12px',
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '13px',
  },
  officeLogo: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    color: tokens.colorNeutralForeground1,
    fontSize: '16px',
    fontWeight: tokens.fontWeightBold,
    fontFamily: 'Segoe UI, sans-serif',
    letterSpacing: '-0.16px',
    lineHeight: 'normal',
  },
  content: {
    width: '1024px',
    margin: '0 auto',
    padding: '48px 0 80px',
    display: 'flex',
    flexDirection: 'column',
    gap: '48px',
    '@media (max-width: 1200px)': {
      width: '100%',
      padding: '32px 60px 60px',
    },
    '@media (max-width: 768px)': {
      padding: '24px 20px 40px',
    },
  },
  startFromScratchSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 700,
    fontFamily: "'Segoe Sans', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    fontVariationSettings: '"opsz" 36',
    lineHeight: '32px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  customCanvasCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: '12px',
    padding: '16px 24px 16px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow4,
    },
  },
  customCanvasFavicon: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '64px',
    height: '64px',
    flexShrink: 0,
  },
  customCanvasText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  customCanvasTitle: {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  customCanvasDescription: {
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground4,
    margin: 0,
  },
  chevronIcon: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  latestUpdatesSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    paddingBottom: '16px',
  },
  changelogCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: '12px',
    padding: '16px 24px 16px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    width: '100%',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow4,
    },
  },
  changelogFavicon: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    flexShrink: 0,
  },
  changelogText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  changelogTitle: {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  changelogDescription: {
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground4,
    margin: 0,
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  updateBadge: {
    border: '1px solid #d2e0f4',
    borderRadius: '9999px',
    padding: '6px 12px 6px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },
  updateBadgeText: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '20px',
    color: tokens.colorBrandForeground1,
    margin: 0,
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  documentationSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
  },
  documentationGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    width: '100%',
  },
  documentationCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: '12px',
    padding: '16px 24px 16px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    flex: '1 1 0',
    minWidth: 0,
    '@media (max-width: 768px)': {
      flex: '1 1 100%',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow4,
    },
  },
  gettingStartedFavicon: {
    backgroundColor: '#ebf3fc',
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    flexShrink: 0,
  },
  feedbackFavicon: {
    backgroundColor: '#fdf6f6',
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    flexShrink: 0,
  },
  documentationText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  documentationTitle: {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  documentationDescription: {
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground4,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  designContributorsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
  },
  contributorsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    width: '100%',
    justifyContent: 'flex-start',
  },
  contributorCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    width: '500px',
    '@media (max-width: 1200px)': {
      width: '100%',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow4,
    },
  },
  contributorAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  contributorInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  contributorName: {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  contributorTitle: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '20px',
    color: tokens.colorNeutralForeground4,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '4px',
    flexShrink: 0,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(15, 108, 189, 0.1)',
    },
  },
  chatButtonText: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '20px',
    color: tokens.colorBrandForeground1,
    margin: 0,
  },
  officeAppsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
    width: '100%',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
  officeCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: '12px',
    padding: '16px 24px 16px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    width: '500px',
    '@media (max-width: 1200px)': {
      width: '100%',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow4,
    },
  },
  officeIcon: {
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    flexShrink: 0,
  },
  officeText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  officeTitle: {
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  officeDescription: {
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    lineHeight: '22px',
    color: tokens.colorNeutralForeground4,
    margin: 0,
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  themeToggle: {
    display: 'flex',
    alignItems: 'center',
  },
});

function App() {
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [version, setVersion] = useState('1.0.0');
  const [lastUpdateDate, setLastUpdateDate] = useState('Sept 26');

  // Get current version from package.json and format current date
  useEffect(() => {
    // Set version from version.ts file
    setVersion(APP_VERSION);
    
    // Set current date in readable format
    const today = new Date();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"
    ];
    const formattedDate = `${monthNames[today.getMonth()]} ${today.getDate()}`;
    setLastUpdateDate(formattedDate);
  }, []);
  const styles = useStyles();
  const theme: Theme = isDarkTheme ? webDarkTheme : webLightTheme;

    const handleOfficeAppClick = (app: string) => {
    const appRoutes = {
      'Word': '/word-silhouette',
      'Forms': '/forms-silhouette'
    };
    
    const route = appRoutes[app as keyof typeof appRoutes];
    if (route) {
      window.open(route, '_blank');
    }
  };

  const handleContributorChatClick = (contributor: string) => {
    const chatUrls = {
      'Kaela Disney': 'https://teams.microsoft.com/l/chat/48:notes/conversations?context=%7B%22contextType%22%3A%22chat%22%7D',
      'Kyle Anderson': 'https://teams.microsoft.com/l/chat/19:414b321d-a5d2-4c41-870c-abe7beb4f129_ac6ab171-09c8-4e8b-a630-c27d009d3d07@unq.gbl.spaces/conversations?context=%7B%22contextType%22%3A%22chat%22%7D'
    };
    
    const url = chatUrls[contributor as keyof typeof chatUrls];
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleGettingStartedClick = () => {
    const url = 'https://microsoft.sharepoint-df.com/:fl:/g/contentstorage/CSP_4be9956a-890e-4a3e-b2be-e42ba0ec5ec5/EZ8wcYj33-JNuzGMGRZhJVwB7SMlSF_e6Nli2iyA17kt8w?e=hT5dnm&nav=cz0lMkZjb250ZW50c3RvcmFnZSUyRkNTUF80YmU5OTU2YS04OTBlLTRhM2UtYjJiZS1lNDJiYTBlYzVlYzUmZD1iJTIxYXBYcFN3NkpQa3F5dnVRcm9PeGV4WURSS2xtdTFrSlBudl9iZEtWSUhDSldGcFVfWk40UVFhVkNQWmZIbDVhbiZmPTAxMk1UNTJCNDdHQllZUjU2NzRKRzNXTU1NREVMR0NKSzQmYz0lMkYmYT1Mb29wQXBwJnA9JTQwZmx1aWR4JTJGbG9vcC1wYWdlLWNvbnRhaW5lciZ4PSU3QiUyMnclMjIlM0ElMjJUMFJUVUh4dGFXTnliM052Wm5RdWMyaGhjbVZ3YjJsdWRDMWtaaTVqYjIxOFlpRmhjRmh3VTNjMlNsQnJjWGwyZFZGeWIwOTRaWGhaUkZKTGJHMTFNV3RLVUc1MlgySmtTMVpKU0VOS1YwWndWVjlhVGpSUlVXRldRMUJhWmtoc05XRnVmREF4TWsxVU5USkNOVFkyTkRSUVRsVkZWMHBDUVRNeU0xTk9SVTlIUmtnMU1rNCUzRCUyMiUyQyUyMmklMjIlM0ElMjJmNDI5MmJmOS1kZDI0LTRiOWUtYjFjNS0yMjgzNTVhMjg2OGQlMjIlN0Q%3D';
    window.open(url, '_blank');
  };

  const handleChangelogClick = () => {
    const url = 'https://dev.azure.com/microsoftdesign/OPG%20Design%20Lab/_git/boilerplate-officeapp-playground/commits';
    window.open(url, '_blank');
  };

  const handleFeedbackClick = () => {
    const url = 'https://forms.office.com/r/pjiunhiZ9K';
    window.open(url, '_blank');
  };

  const handleBlankClick = () => {
    console.log('Starting from blank');
    // Navigate to blank silhouette page in new tab
    window.open('/blank-silhouette', '_blank');
  };

  return (
    <FluentProvider theme={theme}>
      <div className={styles.container}>

        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.officeLogo}>
              <OfficeLogo isDark={isDarkTheme} />
            </div>
            <Text className={styles.headerTitle}>Office Boilerplate Sandbox</Text>
          </div>
          <div className={styles.themeToggle}>
            <Switch
              checked={isDarkTheme}
              onChange={(_, data) => setIsDarkTheme(data.checked)}
              label={isDarkTheme ? 'Dark Mode' : 'Light Mode'}
            />
          </div>
        </header>

        {/* Main Content */}
        <main className={styles.content}>
          {/* Start from scratch Section */}
          <section className={styles.startFromScratchSection}>
            <h2 className={styles.sectionTitle}>Start from scratch</h2>
            <div className={styles.customCanvasCard} onClick={handleBlankClick}>
              <div className={styles.customCanvasFavicon}>
                <Code20Regular style={{ width: '32px', height: '32px' }} />
              </div>
              <div className={styles.customCanvasText}>
                <h3 className={styles.customCanvasTitle}>Custom canvas</h3>
                <p className={styles.customCanvasDescription}>
                  An empty workspace for building and testing your own ideas from scratch.
                </p>
              </div>
              <div className={styles.chevronIcon}>
                <ChevronRight20Regular />
              </div>
            </div>
          </section>

          {/* Apps Section */}
          <section className={styles.startFromScratchSection}>
            <h2 className={styles.sectionTitle}>Apps</h2>
            <div className={styles.officeAppsGrid}>
            {/* Word */}
            <div className={styles.officeCard} onClick={() => handleOfficeAppClick('Word')}>
              <div className={styles.officeIcon}>
                <WordIcon />
              </div>
              <div className={styles.officeText}>
                <h3 className={styles.officeTitle}>Word</h3>
                <div className={styles.officeDescription}>
                  <span>Build on top of the Word silhouette.</span>
                </div>
              </div>
              <div className={styles.chevronIcon}>
                <ChevronRight20Regular />
              </div>
            </div>

            {/* PowerPoint - Removed */}

            {/* Excel - Removed */}

            {/* OneNote - Removed */}

            {/* Forms */}
            <div className={styles.officeCard} onClick={() => handleOfficeAppClick('Forms')}>
              <div className={styles.officeIcon}>
                <FormsIcon />
              </div>
              <div className={styles.officeText}>
                <h3 className={styles.officeTitle}>Forms</h3>
                <div className={styles.officeDescription}>
                  <span>Build on top of the Forms silhouette.</span>
                </div>
              </div>
              <div className={styles.chevronIcon}>
                <ChevronRight20Regular />
              </div>
            </div>
          </div>
          </section>

          {/* Latest updates Section */}
          <section className={styles.latestUpdatesSection}>
            <h2 className={styles.sectionTitle}>Latest updates</h2>
            <div className={styles.changelogCard} onClick={handleChangelogClick} style={{ cursor: 'pointer' }}>
              <div className={styles.changelogFavicon}>
                <TextBulletListTreeRegular style={{ width: '32px', height: '32px' }} />
              </div>
              <div className={styles.changelogText}>
                <h3 className={styles.changelogTitle}>Changelog</h3>
                <div className={styles.changelogDescription}>
                  <span>View latest changes in</span>
                  <span>v{version}</span>
                </div>
              </div>
              <div className={styles.updateBadge}>
                <History20Regular style={{ color: '#0F6CBD' }} />
                <div className={styles.updateBadgeText}>
                  <span>Updated</span>
                  <span>{lastUpdateDate}</span>
                </div>
              </div>
              <div className={styles.chevronIcon}>
                <ChevronRight20Regular />
              </div>
            </div>
          </section>

          {/* Documentation Section */}
          <section className={styles.documentationSection}>
            <h2 className={styles.sectionTitle}>Documentation</h2>
            <div className={styles.documentationGrid}>
              <div className={styles.documentationCard} onClick={handleGettingStartedClick} style={{ cursor: 'pointer' }}>
                <div className={styles.gettingStartedFavicon}>
                  <CompassNorthwestFilled 
                    style={{ 
                      width: '32px', 
                      height: '32px',
                      color: '#0F6CBD'
                    }} 
                  />
                </div>
                <div className={styles.documentationText}>
                  <h3 className={styles.documentationTitle}>Getting Started</h3>
                  <p className={styles.documentationDescription}>
                    Learn about how to use this boilerplate.
                  </p>
                </div>
                <div className={styles.chevronIcon}>
                  <ChevronRight20Regular />
                </div>
              </div>
              <div className={styles.documentationCard} onClick={handleFeedbackClick} style={{ cursor: 'pointer' }}>
                <div className={styles.feedbackFavicon}>
                  <PersonFeedbackFilled 
                    style={{ 
                      width: '32px', 
                      height: '32px',
                      color: '#D13438'
                    }} 
                  />
                </div>
                <div className={styles.documentationText}>
                  <h3 className={styles.documentationTitle}>Feedback & Contribution</h3>
                  <p className={styles.documentationDescription}>
                    Provide feedback or help contribute to this boilerplate.
                  </p>
                </div>
                <div className={styles.chevronIcon}>
                  <ChevronRight20Regular />
                </div>
              </div>
            </div>
          </section>

          {/* Design Contributors Section */}
          <section className={styles.designContributorsSection}>
            <h2 className={styles.sectionTitle}>Design contributors</h2>
            <div className={styles.contributorsGrid}>
              <div className={styles.contributorCard} onClick={() => handleContributorChatClick('Kaela Disney')} style={{ cursor: 'pointer' }}>
                <img 
                  src={kdisneyPhoto} 
                  alt="Kaela Disney" 
                  className={styles.contributorAvatar}
                />
                <div className={styles.contributorInfo}>
                  <h3 className={styles.contributorName}>Kaela Disney</h3>
                  <p className={styles.contributorTitle}>PRODUCT DESIGNER 2 · OPG UX Design US</p>
                </div>
                <button className={styles.chatButton}>
                  <Chat20Regular style={{ color: '#0F6CBD' }} />
                  <span className={styles.chatButtonText}>Chat</span>
                </button>
              </div>
              <div className={styles.contributorCard} onClick={() => handleContributorChatClick('Kyle Anderson')} style={{ cursor: 'pointer' }}>
                <img 
                  src={kandersonPhoto} 
                  alt="Kyle Anderson" 
                  className={styles.contributorAvatar}
                />
                <div className={styles.contributorInfo}>
                  <h3 className={styles.contributorName}>Kyle Anderson</h3>
                  <p className={styles.contributorTitle}>PRINCIPAL PRODUCT DESIGNER · OPG UX DESIGN US</p>
                </div>
                <button className={styles.chatButton}>
                  <Chat20Regular style={{ color: '#0F6CBD' }} />
                  <span className={styles.chatButtonText}>Chat</span>
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>
    </FluentProvider>
  );
}

export default App;
